import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { timeZoneDisplayByIana } from 'src/content/applications/quote/timezoneResolver';

dayjs.extend(utc);
dayjs.extend(timezone);

// Match the backend's retained-shift fallback, never the viewer's timezone.
export function resolveAttendanceTimezone(record: any): string {
  const shift = record?.shift ?? record;
  const event = shift?.Event ?? record?.Event;
  const candidates = [
    event?.IANATimeZone,
    record?.IANATimeZone,
    shift?.timezone,
    event?.timezone,
    record?.job?.timezone,
    record?.jobs?.[0]?.job?.timezone
  ];
  for (const value of candidates) {
    if (!value || typeof value !== 'string') continue;
    try {
      new Intl.DateTimeFormat('en', { timeZone: value });
      return value;
    } catch {}
    const mapped = Object.entries(timeZoneDisplayByIana).find(
      ([, label]) => label === value
    );
    if (mapped) return mapped[0];
    const aliases: Record<string, string> = {
      eastern: 'America/New_York',
      central: 'America/Chicago',
      mountain: 'America/Denver',
      pacific: 'America/Los_Angeles',
      est: 'America/New_York',
      edt: 'America/New_York',
      cst: 'America/Chicago',
      cdt: 'America/Chicago',
      mst: 'America/Denver',
      mdt: 'America/Denver',
      pst: 'America/Los_Angeles',
      pdt: 'America/Los_Angeles'
    };
    const match = Object.entries(aliases).find(([key]) =>
      value.toLowerCase().includes(key)
    );
    if (match) return match[1];
  }
  return 'America/New_York';
}

export function attendanceTime(value: string, record: any) {
  const zone = resolveAttendanceTimezone(record);
  if (!/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return dayjs.tz(value, zone);
  // Dayjs instance.tz can shift an instant during a fold in the browser's own
  // zone. Derive its actual zone offset from Intl, then retain that wall time.
  const offsetLabel =
    new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'longOffset'
    })
      .formatToParts(new Date(value))
      .find((part) => part.type === 'timeZoneName')?.value || 'GMT';
  const match = offsetLabel.match(/GMT([+-])(\d{2}):(\d{2})/);
  const offset = match
    ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]))
    : 0;
  const wall = dayjs
    .utc(value)
    .utcOffset(offset)
    .format('YYYY-MM-DD HH:mm:ss.SSS');
  return dayjs.tz(wall, zone).utcOffset(offset, true);
}

/** Convert a wall-time edit to an instant without reinterpreting it in the browser zone. */
export function attendanceInput(
  time: string | null,
  shift: any,
  item: any,
  field: 'clockIn' | 'clockOut',
  explicitDate?: string
): string | null {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
  const zone = resolveAttendanceTimezone(item);
  const previous = item?.job?.[field];
  if (explicitDate !== undefined && (
    !/^\d{4}-\d{2}-\d{2}$/.test(explicitDate) ||
    dayjs.utc(explicitDate).format('YYYY-MM-DD') !== explicitDate
  )) return null;
  let date = explicitDate ?? (previous
    ? attendanceTime(previous, item).format('YYYY-MM-DD')
    : String(shift.startDate).slice(0, 10));
  // Preserve either occurrence of a repeated fall-back hour on an unchanged edit.
  if (previous && attendanceTime(previous, item).format('HH:mm') === time &&
    attendanceTime(previous, item).format('YYYY-MM-DD') === date)
    return attendanceTime(previous, item).toISOString();
  let parsed = dayjs.tz(`${date} ${time}`, zone);
  if (parsed.format('YYYY-MM-DD HH:mm') !== `${date} ${time}`) return null;
  if (
    field === 'clockOut' &&
    explicitDate === undefined &&
    !previous &&
    item?.job?.clockIn &&
    parsed.isBefore(attendanceTime(item.job.clockIn, item))
  ) {
    date = dayjs.utc(date).add(1, 'day').format('YYYY-MM-DD');
    parsed = dayjs.tz(`${date} ${time}`, zone);
    if (parsed.format('YYYY-MM-DD HH:mm') !== `${date} ${time}`) return null;
  }
  if (explicitDate !== undefined) {
    const other = item?.job?.[field === 'clockIn' ? 'clockOut' : 'clockIn'];
    if (other && (field === 'clockIn'
      ? parsed.isAfter(attendanceTime(other, item))
      : parsed.isBefore(attendanceTime(other, item)))) return null;
  }
  return parsed.toISOString();
}
